import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, AlertTriangle, Wallet, Receipt,
  ArrowDownCircle, ArrowUpCircle, Calendar, RefreshCw, ChevronDown,
  Mail, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart, Line, Cell,
} from "recharts";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

const COLOR_INGRESO = "#205c45";
const COLOR_EGRESO = "#a3322a";
const COLOR_GASTO = "#c8943f";
const COLOR_NEUTRO = "#5c7368";

function KpiCard({ titulo, principal, secundario, tercero, icono: Icon, tone, testid }) {
  const tones = {
    green: "from-[#e9f2ee] to-white border-[#205c45]/30 text-[#205c45]",
    red: "from-[#fbeaea] to-white border-[#a3322a]/30 text-[#a3322a]",
    amber: "from-[#fef7ec] to-white border-[#c8943f]/40 text-[#8a5500]",
    blue: "from-[#eaf1f7] to-white border-[#345b80]/30 text-[#345b80]",
  };
  return (
    <Card className={`p-5 border bg-gradient-to-br ${tones[tone] || tones.green}`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="rounded-full bg-white/70 p-2 shadow-sm">
          <Icon size={20} />
        </div>
        <span className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{titulo}</span>
      </div>
      <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
        {principal}
      </div>
      {secundario && <div className="text-xs mt-1 text-[#5c7368]">{secundario}</div>}
      {tercero && <div className="text-xs mt-0.5 text-[#5c7368]">{tercero}</div>}
    </Card>
  );
}

export default function SaludFinanciera() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [showDetailIngresos, setShowDetailIngresos] = useState(false);
  const [showDetailEgresos, setShowDetailEgresos] = useState(false);
  const [enviandoId, setEnviandoId] = useState(null);

  const enviarRecordatorio = async (clienteId, clienteNombre) => {
    if (!clienteId || clienteId === "—") {
      toast.error("Cliente sin ID válido");
      return;
    }
    if (!window.confirm(`¿Enviar recordatorio de cobro consolidado a ${clienteNombre}?\n\nSe enviará un email con todas sus facturas pendientes.`)) {
      return;
    }
    setEnviandoId(clienteId);
    try {
      const res = await api.post(`/cxc-reminders/enviar-cliente/${clienteId}`);
      toast.success(
        `✓ Email enviado a ${res.data.email_to}`,
        { description: `${res.data.n_facturas} factura(s) · $${Number(res.data.total_adeudado).toLocaleString("es-EC", { minimumFractionDigits: 2 })}` }
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviandoId(null);
    }
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard/salud-financiera?anio=${anio}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [anio]);

  const datosBarrasGastos = useMemo(() => {
    if (!data) return [];
    return data.gastos_operativos.por_mes.map((m) => ({
      mes: m.mes,
      Pagado: m.pagado,
      Pendiente: m.pendiente,
    }));
  }, [data]);

  const datosFlujoMensual = useMemo(() => {
    if (!data) return [];
    return data.resumen_mensual.map((m) => ({
      mes: m.mes,
      Cobros: m.cobros,
      Pagos: -Math.abs(m.pagos),
      Gastos: -Math.abs(m.gastos_op),
      Neto: m.flujo_neto,
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="p-10 text-center text-[#5c7368]" data-testid="salud-financiera-loading">
        Calculando salud financiera...
      </div>
    );
  }
  if (!data) return null;

  const { cxc, cxp, gastos_operativos: go, flujo_30d: f30 } = data;

  return (
    <div className="space-y-6" data-testid="salud-financiera-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Salud Financiera
          </h1>
          <p className="text-sm text-[#5c7368] mt-1">
            Vista 360° de cobros, pagos, gastos y flujo proyectado · Año {data.anio} · Corte {data.fecha_corte}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={anio}
            onChange={(e) => setAnio(parseInt(e.target.value))}
            className="border border-[#d1dbd6] rounded-md px-3 py-2 text-sm bg-white"
            data-testid="salud-anio-select"
          >
            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={cargar} data-testid="salud-refresh-btn">
            <RefreshCw size={14} className="mr-1.5" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-cxc-pendiente"
          titulo="CxC Pendiente (total)"
          principal={fmt(cxc.pendiente)}
          secundario={`${cxc.n_pendientes} facturas abiertas`}
          tercero={cxc.vencido > 0 ? `⚠ Vencido: ${fmt(cxc.vencido)} (${cxc.n_vencidas})` : "✓ Sin vencidas"}
          icono={ArrowDownCircle}
          tone="green"
        />
        <KpiCard
          testid="kpi-cxp-pendiente"
          titulo="CxP Pendiente (total)"
          principal={fmt(cxp.pendiente)}
          secundario={`${cxp.n_pendientes} facturas abiertas`}
          tercero={cxp.vencido > 0 ? `⚠ Vencido: ${fmt(cxp.vencido)} (${cxp.n_vencidas})` : "✓ Sin vencidas"}
          icono={ArrowUpCircle}
          tone="red"
        />
        <KpiCard
          testid="kpi-gastos-anual"
          titulo="Gastos Operativos"
          principal={fmt(go.total_anual)}
          secundario={`${go.n_gastos} registros · ${go.total_pagado > 0 ? Math.round(go.total_pagado / go.total_anual * 100) : 0}% pagados`}
          tercero={`Pendientes: ${fmt(go.total_pendiente)}`}
          icono={Receipt}
          tone="amber"
        />
        <KpiCard
          testid="kpi-flujo-proyectado"
          titulo="Flujo Proyectado 30d"
          principal={
            <span className={f30.saldo_proyectado >= 0 ? "text-[#205c45]" : "text-[#a3322a]"}>
              {fmt(f30.saldo_proyectado)}
            </span>
          }
          secundario={`Ingresos: ${fmt(f30.ingresos_esperados)}`}
          tercero={`Egresos: ${fmt(f30.egresos_esperados)}`}
          icono={Wallet}
          tone={f30.saldo_proyectado >= 0 ? "blue" : "red"}
        />
      </div>

      {/* Comparativos CxC vs CxP */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-[#d1dbd6]" data-testid="cxc-detail-card">
          <h3 className="text-sm font-semibold text-[#15241d] mb-3 flex items-center gap-2">
            <ArrowDownCircle size={16} className="text-[#205c45]" /> Cuentas por Cobrar
          </h3>
          <div className="space-y-2 text-sm">
            <div className="text-[11px] uppercase tracking-wider text-[#5c7368] font-semibold">Acumulado histórico</div>
            <div className="flex justify-between"><span className="text-[#5c7368]">Pendiente total</span><span className="font-bold">{fmt(cxc.pendiente)}</span></div>
            <div className="flex justify-between"><span className="text-[#a3322a] font-medium">Vencido</span><span className="text-[#a3322a] font-bold">{fmt(cxc.vencido)}</span></div>
            <div className="text-[11px] uppercase tracking-wider text-[#5c7368] font-semibold pt-2 border-t">Año {data.anio}</div>
            <div className="flex justify-between"><span className="text-[#5c7368]">Emitido</span><span className="font-medium">{fmt(cxc.total_emitido)}</span></div>
            <div className="flex justify-between"><span className="text-[#5c7368]">Cobrado</span><span className="font-medium text-[#205c45]">{fmt(cxc.cobrado)}</span></div>
          </div>
          <div className="mt-3">
            <div className="h-2 bg-[#e9f2ee] rounded-full overflow-hidden flex">
              <div className="bg-[#205c45]" style={{ width: `${cxc.tasa_cobranza_pct}%` }} />
              <div className="bg-[#a3322a]" style={{ width: `${cxc.total_emitido > 0 ? (cxc.vencido / cxc.total_emitido * 100) : 0}%` }} />
            </div>
            <div className="text-xs text-[#5c7368] mt-1">{cxc.tasa_cobranza_pct}% cobrado año {data.anio}</div>
          </div>
          <Link to="/contabilidad" className="text-xs text-[#205c45] underline mt-3 inline-block">Ver detalle CxC →</Link>
        </Card>

        <Card className="p-5 border border-[#d1dbd6]" data-testid="cxp-detail-card">
          <h3 className="text-sm font-semibold text-[#15241d] mb-3 flex items-center gap-2">
            <ArrowUpCircle size={16} className="text-[#a3322a]" /> Cuentas por Pagar
          </h3>
          <div className="space-y-2 text-sm">
            <div className="text-[11px] uppercase tracking-wider text-[#5c7368] font-semibold">Acumulado histórico</div>
            <div className="flex justify-between"><span className="text-[#5c7368]">Pendiente total</span><span className="font-bold">{fmt(cxp.pendiente)}</span></div>
            <div className="flex justify-between"><span className="text-[#a3322a] font-medium">Vencido</span><span className="text-[#a3322a] font-bold">{fmt(cxp.vencido)}</span></div>
            <div className="text-[11px] uppercase tracking-wider text-[#5c7368] font-semibold pt-2 border-t">Año {data.anio}</div>
            <div className="flex justify-between"><span className="text-[#5c7368]">Emitido</span><span className="font-medium">{fmt(cxp.total_emitido)}</span></div>
            <div className="flex justify-between"><span className="text-[#5c7368]">Pagado</span><span className="font-medium text-[#205c45]">{fmt(cxp.pagado)}</span></div>
          </div>
          <div className="mt-3">
            <div className="h-2 bg-[#fbeaea] rounded-full overflow-hidden flex">
              <div className="bg-[#205c45]" style={{ width: `${cxp.tasa_pago_pct}%` }} />
              <div className="bg-[#a3322a]" style={{ width: `${cxp.total_emitido > 0 ? (cxp.vencido / cxp.total_emitido * 100) : 0}%` }} />
            </div>
            <div className="text-xs text-[#5c7368] mt-1">{cxp.tasa_pago_pct}% pagado año {data.anio}</div>
          </div>
          <Link to="/contabilidad" className="text-xs text-[#205c45] underline mt-3 inline-block">Ver detalle CxP →</Link>
        </Card>
      </div>

      {/* Top Deudores / Acreedores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-[#d1dbd6]" data-testid="top-deudores-card">
          <h3 className="text-sm font-semibold text-[#15241d] mb-1 flex items-center gap-2">
            <ArrowDownCircle size={16} className="text-[#205c45]" /> Top 10 Deudores
          </h3>
          <p className="text-xs text-[#5c7368] mb-4">Clientes con mayor saldo pendiente · histórico</p>
          {data.top_deudores && data.top_deudores.length > 0 ? (
            <div className="space-y-1.5">
              {data.top_deudores.map((d, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-[#e6ece9] last:border-b-0">
                  <span className="text-xs font-bold text-[#5c7368] w-5">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-[#15241d] truncate" title={d.cliente}>{d.cliente}</div>
                    <div className="text-[10px] text-[#5c7368]">
                      {d.n} factura(s){d.vencido > 0 && <span className="text-[#a3322a] ml-1">· {fmt(d.vencido)} vencido</span>}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[#205c45] tabular-nums whitespace-nowrap">{fmt(d.saldo)}</span>
                  <button
                    onClick={() => enviarRecordatorio(d.cliente_id, d.cliente)}
                    disabled={enviandoId === d.cliente_id || !d.cliente_id || d.cliente_id === "—"}
                    title="Enviar recordatorio de cobro por email"
                    className="p-1.5 rounded hover:bg-[#e9f2ee] text-[#205c45] disabled:opacity-40 disabled:cursor-not-allowed transition"
                    data-testid={`btn-recordatorio-${i}`}
                  >
                    {enviandoId === d.cliente_id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Mail size={14} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-[#5c7368] py-4 text-center">Sin deudas pendientes</div>
          )}
        </Card>

        <Card className="p-5 border border-[#d1dbd6]" data-testid="top-acreedores-card">
          <h3 className="text-sm font-semibold text-[#15241d] mb-1 flex items-center gap-2">
            <ArrowUpCircle size={16} className="text-[#a3322a]" /> Top 10 Acreedores
          </h3>
          <p className="text-xs text-[#5c7368] mb-4">Proveedores a los que más se debe · histórico</p>
          {data.top_acreedores && data.top_acreedores.length > 0 ? (
            <div className="space-y-1.5">
              {data.top_acreedores.map((d, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-[#e6ece9] last:border-b-0">
                  <span className="text-xs font-bold text-[#5c7368] w-5">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-[#15241d] truncate" title={d.proveedor}>{d.proveedor}</div>
                    <div className="text-[10px] text-[#5c7368]">
                      {d.n} factura(s){d.vencido > 0 && <span className="text-[#a3322a] ml-1">· {fmt(d.vencido)} vencido</span>}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[#a3322a] tabular-nums whitespace-nowrap">{fmt(d.saldo)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-[#5c7368] py-4 text-center">Sin deudas a proveedores</div>
          )}
        </Card>
      </div>

      {/* Gráfico Gastos por mes */}
      <Card className="p-5 border border-[#d1dbd6]" data-testid="gastos-mensuales-chart">
        <h3 className="text-sm font-semibold text-[#15241d] mb-1 flex items-center gap-2">
          <Receipt size={16} className="text-[#c8943f]" /> Gastos Operativos por Mes
        </h3>
        <p className="text-xs text-[#5c7368] mb-4">Distribución mensual · pagado vs pendiente</p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={datosBarrasGastos}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e6ece9" />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtShort} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Bar dataKey="Pagado" stackId="a" fill={COLOR_INGRESO} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Pendiente" stackId="a" fill={COLOR_GASTO} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Gráfico Flujo de Caja Mensual */}
      <Card className="p-5 border border-[#d1dbd6]" data-testid="flujo-mensual-chart">
        <h3 className="text-sm font-semibold text-[#15241d] mb-1 flex items-center gap-2">
          <TrendingUp size={16} className="text-[#205c45]" /> Flujo de Caja Mensual
        </h3>
        <p className="text-xs text-[#5c7368] mb-4">Cobros (entradas) vs Pagos + Gastos (salidas) · Línea = Flujo neto</p>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={datosFlujoMensual}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e6ece9" />
            <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtShort} />
            <Tooltip formatter={(v) => fmt(Math.abs(v))} />
            <Legend />
            <Bar dataKey="Cobros" fill={COLOR_INGRESO} />
            <Bar dataKey="Pagos" fill={COLOR_EGRESO} />
            <Bar dataKey="Gastos" fill={COLOR_GASTO} />
            <Line type="monotone" dataKey="Neto" stroke="#15241d" strokeWidth={2} dot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* Flujo proyectado 30 días - detalle */}
      <Card className="p-5 border-2 border-[#205c45]/30 bg-gradient-to-br from-[#f5fbf8] to-white" data-testid="flujo-30d-detail">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-[#15241d] flex items-center gap-2">
              <Calendar size={18} className="text-[#205c45]" /> Flujo Proyectado · Próximos 30 días
            </h3>
            <p className="text-xs text-[#5c7368] mt-1">Hasta el {f30.horizonte}</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#5c7368]">Saldo proyectado</div>
            <div className={`text-2xl font-bold ${f30.saldo_proyectado >= 0 ? "text-[#205c45]" : "text-[#a3322a]"}`}>
              {fmt(f30.saldo_proyectado)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-lg p-3 border border-[#205c45]/20">
            <div className="text-xs text-[#5c7368] mb-1">Ingresos esperados (CxC)</div>
            <div className="text-lg font-bold text-[#205c45]">{fmt(f30.ingresos_esperados)}</div>
            <div className="text-[10px] text-[#5c7368]">{f30.detalle_ingresos.length} factura(s) que vencen</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-[#a3322a]/20">
            <div className="text-xs text-[#5c7368] mb-1">Egresos CxP</div>
            <div className="text-lg font-bold text-[#a3322a]">{fmt(f30.egresos_cxp)}</div>
            <div className="text-[10px] text-[#5c7368]">{f30.detalle_egresos_cxp.length} factura(s) que vencen</div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-[#c8943f]/30">
            <div className="text-xs text-[#5c7368] mb-1">Egresos Gastos Op.</div>
            <div className="text-lg font-bold text-[#8a5500]">{fmt(f30.egresos_gastos_op)}</div>
            <div className="text-[10px] text-[#5c7368]">{f30.detalle_egresos_gastos.length} gasto(s) pendiente(s)</div>
          </div>
        </div>

        {f30.detalle_ingresos.length > 0 && (
          <div className="border-t pt-3 mt-3">
            <button
              className="w-full text-left flex items-center justify-between text-xs font-semibold text-[#205c45]"
              onClick={() => setShowDetailIngresos((v) => !v)}
              data-testid="toggle-detalle-ingresos"
            >
              <span>Ver detalle de ingresos próximos ({f30.detalle_ingresos.length})</span>
              <ChevronDown size={14} className={`transition-transform ${showDetailIngresos ? "rotate-180" : ""}`} />
            </button>
            {showDetailIngresos && (
              <div className="mt-2 space-y-1 max-h-60 overflow-auto">
                {f30.detalle_ingresos.map((it) => (
                  <div key={it.id} className="flex justify-between text-xs py-1 border-b border-[#e6ece9]">
                    <span className="text-[#15241d] truncate flex-1">{it.cliente || it.documento}</span>
                    <span className="text-[#5c7368] mx-2">{it.fecha_vencimiento}</span>
                    <span className="font-medium text-[#205c45]">{fmt(it.saldo)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {(f30.detalle_egresos_cxp.length + f30.detalle_egresos_gastos.length) > 0 && (
          <div className="border-t pt-3 mt-3">
            <button
              className="w-full text-left flex items-center justify-between text-xs font-semibold text-[#a3322a]"
              onClick={() => setShowDetailEgresos((v) => !v)}
              data-testid="toggle-detalle-egresos"
            >
              <span>Ver detalle de egresos próximos ({f30.detalle_egresos_cxp.length + f30.detalle_egresos_gastos.length})</span>
              <ChevronDown size={14} className={`transition-transform ${showDetailEgresos ? "rotate-180" : ""}`} />
            </button>
            {showDetailEgresos && (
              <div className="mt-2 space-y-1 max-h-60 overflow-auto">
                {f30.detalle_egresos_cxp.map((it) => (
                  <div key={`cxp-${it.id}`} className="flex justify-between text-xs py-1 border-b border-[#e6ece9]">
                    <span className="text-[#15241d] truncate flex-1">CxP · {it.proveedor || it.documento}</span>
                    <span className="text-[#5c7368] mx-2">{it.fecha_vencimiento}</span>
                    <span className="font-medium text-[#a3322a]">{fmt(it.saldo)}</span>
                  </div>
                ))}
                {f30.detalle_egresos_gastos.map((it, i) => (
                  <div key={`g-${i}`} className="flex justify-between text-xs py-1 border-b border-[#e6ece9]">
                    <span className="text-[#15241d] truncate flex-1">Gasto · {it.proveedor || it.categoria}</span>
                    <span className="text-[#5c7368] mx-2">{it.fecha}</span>
                    <span className="font-medium text-[#8a5500]">{fmt(it.monto)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {f30.saldo_proyectado < 0 && (
          <div className="mt-4 p-3 bg-[#fbeaea] border border-[#a3322a]/30 rounded-md flex items-start gap-2">
            <AlertTriangle size={16} className="text-[#a3322a] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#a3322a]">
              <strong>Atención:</strong> Los egresos proyectados superan los ingresos esperados.
              Considerar acelerar cobranza o renegociar fechas con proveedores.
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
